import {
  CompetitionFormat,
  CompetitionSeriesStatus,
  CompetitionSessionStatus,
  CompetitionStatus,
  PartAggregation,
  PartInputType,
  RankingMethod,
  SortDirection,
} from '@api/competitionsApi/interfaces';
import { ScoreUnit } from '@api/disciplinesApi/interfaces';
import { isEmpty } from '@helpers/formHelpers';
import { translate } from '@translations/i18n';
import { ChipProps } from '@mui/material';

export const getDefaultSortDirectionForScoreUnit = (
  scoreUnit: ScoreUnit
): SortDirection => (scoreUnit === 'time' ? 'asc' : 'desc');

export const getDefaultPartInputTypeForScoreUnit = (
  scoreUnit: ScoreUnit
): PartInputType => (scoreUnit === 'time' ? 'time' : 'numeric');

export const getDefaultPartAggregationForScoreUnit = (
  scoreUnit: ScoreUnit
): PartAggregation => (scoreUnit === 'time' ? 'min' : 'sum');

export const getAllowedPartInputTypesForScoreUnit = (
  scoreUnit: ScoreUnit
): PartInputType[] =>
  scoreUnit === 'time' ? ['time'] : ['numeric', 'target_count'];

export const getManageRankingMethodName = (
  rankingMethod: RankingMethod
): string => {
  return translate(`competitions:manage.labels.rankingMethod.${rankingMethod}`);
};

export const getManageRankingMethodDescription = (
  rankingMethod: RankingMethod
): string => {
  return translate(
    `competitions:rankingRules.rankingMethodOptionDescription.${rankingMethod}`
  );
};

export const getManageSortDirectionName = (
  sortDirection: SortDirection
): string => {
  return translate(`competitions:manage.labels.sortDirection.${sortDirection}`);
};

export const getManageCompetitionStatusName = (
  status: CompetitionStatus
): string => {
  return translate(`competitions:manage.labels.status.${status}`);
};

export const getManageCompetitionFormatName = (
  format: CompetitionFormat
): string => {
  return translate(`competitions:manage.labels.format.${format}`);
};

export const getManageScoreUnitName = (scoreUnit: ScoreUnit): string => {
  return translate(`competitions:manage.labels.scoreUnit.${scoreUnit}`);
};

export const getManagePartAggregationName = (
  partAggregation: PartAggregation
): string => {
  return translate(
    `competitions:manage.labels.partAggregation.${partAggregation}`
  );
};

export const getManagePartInputTypeName = (
  inputType: PartInputType
): string => {
  return translate(`competitions:manage.labels.inputType.${inputType}`);
};

export const canDeleteManageCompetition = (
  status: CompetitionStatus
): boolean => {
  return status === 'draft' || status === 'stopped' || status === 'completed';
};

export const normalizeCompetitionDescription = (
  description?: string
): string | null => {
  if (!description || isEmpty(description.trim())) {
    return null;
  }

  return description;
};

export const getManageCompetitionStatusBadgeColor = (
  status: CompetitionStatus
): ChipProps['color'] => {
  switch (status) {
    case 'open':
      return 'success';
    case 'draft':
      return 'info';
    case 'stopped':
      return 'warning';
    case 'completed':
    default:
      return 'light';
  }
};

export const getManageCompetitionSessionStatusName = (
  status: CompetitionSessionStatus
): string => {
  return translate(
    `competitions:manage.labels.sessionStatus.${status}`
  );
};

export const getManageCompetitionSessionStatusBadgeColor = (
  status: CompetitionSessionStatus
): ChipProps['color'] => {
  switch (status) {
    case 'in_progress':
      return 'success';
    case 'planned':
      return 'info';
    case 'cancelled':
      return 'warning';
    case 'not_held':
      return 'warning';
    case 'completed':
    default:
      return 'light';
  }
};

export const getManageCompetitionSeriesStatusName = (
  status: CompetitionSeriesStatus
): string => {
  return translate(`competitions:manage.labels.seriesStatus.${status}`);
};

export const getManageCompetitionSeriesStatusBadgeColor = (
  status: CompetitionSeriesStatus
): ChipProps['color'] => {
  switch (status) {
    case 'in_progress':
      return 'success';
    case 'planned':
      return 'info';
    case 'completed':
    default:
      return 'light';
  }
};
